import React, { useContext, useEffect, useMemo, useState } from "react";
import { SettingData } from "../../entry/server/app.ts";
import { appRepo } from "../../repo/app-repo.ts";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { AppContext } from "../../context/app-context.tsx";

const inputClass =
  "w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400";

const emptyForm = (): SettingData => ({
  telegram_channel: "",
  telegram_support: "",
  instagram_link: "",
  usdt_fee: 0,
});

export default function SettingsScreen() {
  const { setMessage } = useContext(AppContext);
  const [form, setForm] = useState<SettingData>(emptyForm);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFormValid = useMemo(() => {
    return (
      Boolean(form.telegram_channel.trim()) &&
      Boolean(form.telegram_support.trim()) &&
      Boolean(form.instagram_link.trim()) &&
      Number.isFinite(form.usdt_fee) &&
      form.usdt_fee >= 0
    );
  }, [form]);

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const response = await appRepo.getSettings();
        if (!response.status || !response.data) {
          setMessage({ type: "error", message: response.message || "دریافت تنظیمات ناموفق بود." });
          return;
        }
        setForm({
          telegram_channel: response.data.telegram_channel ?? "",
          telegram_support: response.data.telegram_support ?? "",
          instagram_link: response.data.instagram_link ?? "",
          usdt_fee: Number(response.data.usdt_fee) || 0,
        });
      } catch {
        setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
      } finally {
        setIsLoading(false);
      }
    };

    void fetchSettings();
  }, [setMessage]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isFormValid) {
      setMessage({ type: "error", message: "لطفاً همه فیلدها را کامل کنید." });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: SettingData = {
        telegram_channel: form.telegram_channel.trim(),
        telegram_support: form.telegram_support.trim(),
        instagram_link: form.instagram_link.trim(),
        usdt_fee: Number(form.usdt_fee) || 0,
      };

      const response = await appRepo.updateSettings(payload);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "ذخیره تنظیمات ناموفق بود." });
        return;
      }

      setMessage({ type: "success", message: "تنظیمات با موفقیت ذخیره شد." });
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <section className="space-y-4">
        <AdminPageTitle title="تنظیمات" />
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت تنظیمات...
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <AdminPageTitle title="تنظیمات" />
      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
        <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
          <label className="space-y-2 md:col-span-2">
            <span className="text-sm text-slate-300">کانال تلگرام</span>
            <input
              className={inputClass}
              value={form.telegram_channel}
              onChange={(e) => setForm((prev) => ({ ...prev, telegram_channel: e.target.value }))}
              placeholder="https://t.me/..."
            />
          </label>

          <label className="space-y-2 md:col-span-2">
            <span className="text-sm text-slate-300">پشتیبانی تلگرام</span>
            <input
              className={inputClass}
              value={form.telegram_support}
              onChange={(e) => setForm((prev) => ({ ...prev, telegram_support: e.target.value }))}
              placeholder="https://t.me/..."
            />
          </label>

          <label className="space-y-2 md:col-span-2">
            <span className="text-sm text-slate-300">لینک اینستاگرام</span>
            <input
              className={inputClass}
              value={form.instagram_link}
              onChange={(e) => setForm((prev) => ({ ...prev, instagram_link: e.target.value }))}
              placeholder="https://instagram.com/..."
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm text-slate-300">قیمت دلار</span>
            <input
              type="number"
              min={0}
              step="any"
              className={inputClass}
              value={String(form.usdt_fee)}
              onChange={(e) => {
                const raw = e.target.value;
                const n = raw === "" ? 0 : Number(raw);
                setForm((prev) => ({ ...prev, usdt_fee: Number.isNaN(n) ? 0 : n }));
              }}
            />
          </label>

          <div className="flex items-center gap-3 md:col-span-2">
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "در حال ذخیره…" : "ذخیره تنظیمات"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
